/**
 * React Query READ around POST /api/persona-bundles/:id/simulate
 * (P4.T11 live-preview rail — calling the endpoint shipped in P1.T5,
 * with query semantics rather than a one-shot mutation, so the Preview
 * rail (LivePreviewRail.tsx) can refetch on bundleId/user_message
 * changes with no extra plumbing).
 *
 * Live-on-save: cache key includes `[bundleId, userMessage]`, so Save
 * (which mints a new bundleId) AND user_message edits automatically
 * trigger refetch via React Query's queryKey-change semantics — no
 * useEffect plumbing in the consumer.
 *
 * staleTime: Infinity + refetchOnMount: false → no background or
 * remount-triggered refetches; operator drives all refreshes
 * explicitly (Refresh button) or implicitly (Save mints new bundleId).
 * retry: false → LLM 5xx errors don't auto-retry; expensive to repeat.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type { SimulateResponse } from '@/types/personaWizard'

interface UseSimulatePreviewArgs {
  bundleId: string | null
  userMessage: string
  enabled: boolean
}

export function useSimulatePreview(
  args: UseSimulatePreviewArgs,
): UseQueryResult<SimulateResponse, Error> {
  const { bundleId, userMessage, enabled } = args
  const trimmed = userMessage.trim()
  return useQuery<SimulateResponse, Error>({
    queryKey: ['simulate-preview', bundleId, trimmed],
    queryFn: () =>
      apiClient.post<SimulateResponse>(
        `/api/persona-bundles/${bundleId}/simulate`,
        { user_message: trimmed },
      ),
    enabled: enabled && bundleId !== null && trimmed.length > 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  })
}
